import React, { useCallback, useId, useRef, useState } from 'react';
import { useDismiss } from '../app/useDismiss';

export interface MultiSelectOption<T extends string> {
  value: T;
  label: string;
}

interface MultiSelectProps<T extends string> {
  /** What the control is called for a screen reader ("Severity"). */
  label: string;
  /** Said on the button while nothing is chosen ("All severities"): nothing chosen means no filter. */
  allLabel: string;
  options: readonly MultiSelectOption<T>[];
  value: readonly T[];
  onChange: (next: T[]) => void;
  /** Said on the button when several are chosen ("2 severities"). */
  many?: (count: number) => string;
  /** One choice at a time, as radios: the list closes on a choice (a period has one answer, not several). */
  single?: boolean;
  /** Class of the button besides the select look it shares with the console's selects. */
  className?: string;
}

/**
 * A drop-down in which more than one option can be ticked (client notes 10/26, item 14: the feed's filters). The button
 * has the size, outline and arrow of `.c3-select`; the list under it is plain checkboxes (or radios, with `single`), so
 * Tab and Space reach every option with no key handling of its own. Escape and a press outside close it, as in the
 * top bar's menus (`useDismiss`).
 */
function MultiSelect<T extends string>({
  label,
  allLabel,
  options,
  value,
  onChange,
  many = (count) => `${count} selected`,
  single = false,
  className = '',
}: MultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const close = useCallback(() => setOpen(false), []);
  useDismiss(rootRef, open, close, triggerRef);

  const chosen = options.filter((option) => value.includes(option.value));
  const summary =
    chosen.length === 0 ? allLabel : chosen.length === 1 ? chosen[0].label : many(chosen.length);

  const toggle = (next: T) => {
    if (single) {
      onChange([next]);
      setOpen(false);
      return;
    }
    const keep = new Set(value);
    if (keep.has(next)) keep.delete(next);
    else keep.add(next);
    // Kept in the order of the options, so the address and the button read the same however they were ticked.
    onChange(options.filter((option) => keep.has(option.value)).map((option) => option.value));
  };

  return (
    <div
      className="c3-multi"
      ref={rootRef}
      onBlur={(e) => {
        // Tabbing out of the list closes it; moving between its options does not.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`c3-select c3-multi-btn ${className}`.trim()}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`${label}: ${summary}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        {summary}
      </button>
      {open && (
        <div id={panelId} role="group" aria-label={label} className="c3-multi-panel">
          {single && (
            <label className="c3-multi-opt">
              <input
                type="radio"
                name={panelId}
                checked={chosen.length === 0}
                onChange={() => {
                  onChange([]);
                  setOpen(false);
                }}
              />
              <span>{allLabel}</span>
            </label>
          )}
          {options.map((option) => (
            <label key={option.value} className="c3-multi-opt">
              <input
                type={single ? 'radio' : 'checkbox'}
                name={single ? panelId : undefined}
                checked={value.includes(option.value)}
                onChange={() => toggle(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
          {!single && chosen.length > 0 && (
            <button type="button" className="c3-multi-clear" onClick={() => onChange([])}>
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiSelect;
